/**
 * Карточка товара исполнителя.
 *
 * Форма рассчитана на телефон и на человека, который не заводил карточек
 * товара раньше. Отсюда три решения:
 *
 *   — названия НЕТ. Исполнитель выбирает, что продаёт: «Арбуз» или «Дыня», —
 *     а название на обоих языках берёт сервер из типа. Поля «Название» и
 *     «Название по-узбекски» давали одно осмысленное и одно пустое.
 *   — slug не спрашиваем вовсе, его делает сервер.
 *   — фото снимается камерой прямо здесь (`capture="environment"`), потому что
 *     арбуз лежит рядом, а не в галерее.
 *
 * Ошибки полей приходят с сервера и подставляются как есть — свою валидацию
 * дублировать незачем, разойдётся.
 */
import { useEffect, useRef, useState } from 'react';
import {
  PRODUCT_SIZES,
  type CurrentUser,
  type ProductSize,
  type ProductType,
  type VendorProduct,
} from '@arbuz/shared';
import { ApiError, api } from '../api';
import { makeT } from '../i18n';
import { haptic } from '../tg';
import { dangerButton, secondaryButton } from './courierUi';
import { Field, Toggle, bottomBarStyle, errorTextStyle, hintStyle, pageWithBottomBar } from './vendorUi';

interface Props {
  user: CurrentUser;
  /** null — создаём новый товар. */
  product: VendorProduct | null;
  onSaved: () => void;
  onBack: () => void;
}

export function VendorProductEdit({ user, product, onSaved, onBack }: Props) {
  const t = makeT(user.locale);
  const fileRef = useRef<HTMLInputElement>(null);

  const [types, setTypes] = useState<ProductType[] | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(product?.category_id ?? null);
  const [price, setPrice] = useState(product ? String(Math.round(Number(product.price))) : '');
  const [size, setSize] = useState<ProductSize | ''>(product?.size ?? '');
  const [weightMin, setWeightMin] = useState(product?.weight_min_kg ?? '');
  const [weightMax, setWeightMax] = useState(product?.weight_max_kg ?? '');
  const [description, setDescription] = useState(product?.description ?? '');
  const [available, setAvailable] = useState(product?.is_available ?? true);
  const [imageUrl, setImageUrl] = useState(product?.image_url ?? null);

  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Типы приходят с сервера, а не из словаря: категории заводит администратор,
  // и зашитый в клиент список разошёлся бы с каталогом молча
  useEffect(() => {
    let alive = true;
    api
      .get<ProductType[]>('/api/vendor/product-types')
      .then((loaded) => {
        if (!alive) return;
        setTypes(loaded);
        // У нового товара выбран первый тип: пустой выбор в форме из двух
        // вариантов — лишний шаг, а не свобода
        setCategoryId((current) => current ?? loaded[0]?.id ?? null);
      })
      .catch(() => {
        if (alive) setFields((f) => ({ ...f, category_id: t('productTypesError') }));
      });
    return () => {
      alive = false;
    };
  }, [t]);

  /** Цена вводится целыми сумами, а сервер ждёт numeric(12,2) строкой. */
  const toMoney = (v: string): string => {
    const digits = v.replace(/[^\d.]/g, '');
    return digits.includes('.') ? digits : `${digits || '0'}.00`;
  };

  async function save() {
    setSaving(true);
    setFields({});

    const body: Record<string, unknown> = {
      category_id: categoryId,
      price: toMoney(price),
      size: size === '' ? null : size,
      weight_min_kg: weightMin.trim() ? toMoney(weightMin) : null,
      weight_max_kg: weightMax.trim() ? toMoney(weightMax) : null,
      description: description.trim() ? description.trim() : null,
      is_available: available,
    };
    try {
      const saved = product
        ? await api.patch<VendorProduct>(`/api/vendor/products/${product.id}`, body)
        : await api.post<VendorProduct>('/api/vendor/products', body);

      // Фото могло быть выбрано до того, как товар появился в базе
      const pending = fileRef.current?.files?.[0];
      if (pending && !product) await uploadImage(saved.id, pending);

      haptic('success');
      onSaved();
    } catch (err) {
      haptic('error');
      setFields(err instanceof ApiError ? (err.fields ?? { _: err.message }) : { _: t('actionFailed') });
    } finally {
      setSaving(false);
    }
  }

  async function uploadImage(productId: number, file: File) {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post<{ image_url: string }>(
        `/api/vendor/products/${productId}/image`,
        form,
      );
      setImageUrl(res.image_url);
    } catch (err) {
      setFields((f) => ({
        ...f,
        image: err instanceof ApiError ? err.message : t('productPhotoError'),
      }));
    } finally {
      setUploading(false);
    }
  }

  async function remove() {
    if (!product) return;
    if (!window.confirm(t('productRemoveConfirm'))) return;

    setSaving(true);
    try {
      await api.delete(`/api/vendor/products/${product.id}`);
      haptic('warning');
      onSaved();
    } catch (err) {
      haptic('error');
      setFields({ _: err instanceof ApiError ? err.message : t('actionFailed') });
    } finally {
      setSaving(false);
    }
  }

  const sizeLabel = (s: ProductSize) =>
    s === 's' ? t('productSizeS') : s === 'm' ? t('productSizeM') : t('productSizeL');

  return (
    <main className="page" style={pageWithBottomBar}>
      <h1 className="h1">{t('productEditTitle')}</h1>

      <div style={{ display: 'grid', gap: 'var(--s-4)', marginTop: 'var(--s-4)' }}>
        <div>
          <span style={{ ...hintStyle, display: 'block', margin: '0 0 var(--s-2)' }}>
            {t('productTypeLabel')}
          </span>
          {types === null ? (
            <p className="muted">{t('loading')}</p>
          ) : (
            <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
              {types.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  aria-pressed={categoryId === type.id}
                  onClick={() => setCategoryId(type.id)}
                  style={chipStyle(categoryId === type.id)}
                >
                  {type.name}
                </button>
              ))}
            </div>
          )}
          <p style={{ ...hintStyle, margin: 'var(--s-2) 0 0' }}>{t('productTypeHint')}</p>
          {fields.category_id && (
            <p role="alert" style={errorTextStyle}>
              {fields.category_id}
            </p>
          )}
        </div>

        <Field id="p-price" label={t('productPriceLabel')} error={fields.price}>
          {(p) => (
            <input
              {...p}
              value={price}
              onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ''))}
              inputMode="numeric"
              autoComplete="off"
            />
          )}
        </Field>

        <div>
          <span style={{ ...hintStyle, display: 'block', margin: '0 0 var(--s-2)' }}>
            {t('productSizeLabel')}
          </span>
          <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
            {(['', ...PRODUCT_SIZES] as const).map((s) => (
              <button
                key={s || 'none'}
                type="button"
                aria-pressed={size === s}
                onClick={() => setSize(s as ProductSize | '')}
                style={chipStyle(size === s)}
              >
                {s === '' ? t('productSizeNone') : sizeLabel(s)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <span style={{ ...hintStyle, display: 'block', margin: '0 0 var(--s-2)' }}>
            {t('productWeightLabel')}
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s-3)' }}>
            <Field id="p-wmin" label={t('productWeightFrom')} error={fields.weight_min_kg}>
              {(p) => (
                <input
                  {...p}
                  value={weightMin}
                  onChange={(e) => setWeightMin(e.target.value.replace(/[^\d.]/g, ''))}
                  inputMode="decimal"
                />
              )}
            </Field>
            <Field id="p-wmax" label={t('productWeightTo')} error={fields.weight_max_kg}>
              {(p) => (
                <input
                  {...p}
                  value={weightMax}
                  onChange={(e) => setWeightMax(e.target.value.replace(/[^\d.]/g, ''))}
                  inputMode="decimal"
                />
              )}
            </Field>
          </div>
        </div>

        <Field id="p-desc" label={t('productDescriptionLabel')} error={fields.description}>
          {(p) => (
            <textarea
              {...p}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={3}
              style={{ ...p.style, minHeight: 88, padding: 'var(--s-2) var(--s-3)', resize: 'vertical' }}
            />
          )}
        </Field>

        {/* ── Фото ──────────────────────────────────────────────────────── */}
        <div>
          <span style={{ ...hintStyle, display: 'block', margin: '0 0 var(--s-2)' }}>
            {t('productPhoto')}
          </span>

          {imageUrl && (
            <img
              src={imageUrl}
              alt=""
              style={{
                display: 'block',
                width: '100%',
                maxWidth: 240,
                borderRadius: 'var(--r-md)',
                marginBottom: 'var(--s-2)',
              }}
            />
          )}

          {/* capture="environment" открывает камеру сразу: арбуз лежит рядом,
              а не в галерее */}
          <input
            ref={fileRef}
            id="p-photo"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file && product) void uploadImage(product.id, file);
            }}
          />
          <button
            type="button"
            style={secondaryButton}
            disabled={uploading}
            data-loading={uploading ? 'true' : undefined}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? t('productPhotoUploading') : t('productPhotoAdd')}
          </button>
          {fields.image && (
            <p role="alert" style={errorTextStyle}>
              {fields.image}
            </p>
          )}
        </div>

        <Toggle label={t('productAvailable')} checked={available} onChange={setAvailable} />

        {fields._ && (
          <p role="alert" style={errorTextStyle}>
            {fields._}
          </p>
        )}

        {product && (
          <button type="button" style={dangerButton} disabled={saving} onClick={() => void remove()}>
            {t('productRemove')}
          </button>
        )}
      </div>

      <div style={bottomBarStyle}>
        <button
          type="button"
          className="btn-primary"
          disabled={saving || !categoryId || !price.trim()}
          data-loading={saving ? 'true' : undefined}
          onClick={() => void save()}
        >
          {t('productSave')}
        </button>
        <button type="button" style={secondaryButton} onClick={onBack}>
          {t('back')}
        </button>
      </div>
    </main>
  );
}

/** Таблетка выбора: тип товара и размер выглядят одинаково, значит и стиль один. */
const chipStyle = (active: boolean): React.CSSProperties => ({
  minHeight: 'var(--tap-min)',
  padding: '0 var(--s-4)',
  borderRadius: 'var(--r-full)',
  border: `1px solid ${active ? 'var(--c-primary-text)' : 'var(--c-border-input)'}`,
  background: active ? 'var(--c-primary-subtle)' : 'var(--c-surface)',
  color: active ? 'var(--c-primary-text)' : 'var(--c-text)',
  font: 'inherit',
  cursor: 'pointer',
});
